import { MeasurementMetadata } from '../Measurements/Metadata';
import { DerivedMeasurementCollection, isDerivedMeasurement, Measurement, MeasurementRequirement, MeasurementResult } from "../Measurements/Measurement";
import SelvesterTaskData from "./SelvesterTask";
import { STResolutionTaskData } from './STResolutionTask';

/**
 * A task represents a single unit of work that needs to be done on an image.
 */
export type Task = {
    /**
     * The identifier of the task.
     */
    identifier: TaskIdentifier;

    /**
     * The human-readable name of the task.
     */
    name: string;

    /**
     * The description of the task.
     */
    description: string;
}

/**
 * The runtime data for a task.
 */
export type TaskData = {
    /**
     * The identifier of the task this data is for.
     */
    identifier: TaskIdentifier;
    
    /**
     * The primary, user-defined measurement requirements.
     */
    requirements: MeasurementRequirement[];
    
    /**
     * The derived measurements that can be calculated from the input measurements.
     */
    derivedMeasurements: DerivedMeasurementCollection;
}

export type TaskIdentifier = 'selvester' | 'st-resolution';

/**
 * Whether the given identifier is a task identifier.
 * @param identifier The identifier to check.
 * @returns True if the identifier is a task identifier.
 */
export function isTaskIdentifier(identifier: string): identifier is TaskIdentifier {
    return ['selvester', 'st-resolution'].includes(identifier);
}

/**
 * Get the task with the given identifier.
 * @param identifier The identifier of the task.
 * @returns The task with the given identifier.
 */
export function getTask(identifier: TaskIdentifier): Task {
    switch (identifier) {
        case 'selvester':
            return {
                identifier: 'selvester',
                name: 'Selvester QRS Score',
                description: 'This task calculates the Selvester QRS Score from the given measurements.'
            }
        case 'st-resolution':
            return {
                identifier: 'st-resolution',
                name: 'ST Resolution',
                description: 'This task calculates the ST changes.'
            }
    }
}

/**
 * Get the task data with the given identifier.
 * @param identifier The identifier of the task.
 * @returns The task data with the given identifier.
 */
export function getTaskData(identifier: TaskIdentifier): TaskData {
    switch (identifier) {
        case 'selvester':
            return SelvesterTaskData;
        
        case 'st-resolution':
            return STResolutionTaskData;
    }
}

/**
 * Gets the metadata for the given measurement.
 * @param measurement The measurement to get metadata for.
 * @param taskData The task data to get the metadata from.
 * @returns The metadata for the given measurement, if any.
 */
export function getMetadataForMeasurement(measurement: Measurement, taskData: TaskData): MeasurementMetadata | undefined {
    if(isDerivedMeasurement(measurement)) {
        return taskData.derivedMeasurements.get(measurement.key)?.metadata;
    } else {
        return taskData.requirements.find(req => req.key === measurement.key)?.metadata;
    }
}

/**
 * Gets the metadata for the given measurement result.
 * @param result The measurement result to get metadata for.
 * @param taskData The task data to get the metadata from.
 * @returns The metadata for the given measurement result, if any.
 */
export function getMetadataForResult(result: MeasurementResult, taskData: TaskData): MeasurementMetadata | undefined {
    return getMetadataForMeasurement({ key: result.key, type: result.measurementType }, taskData);
}
